import type { Editor } from "@tiptap/react";

const EDITOR_ONLY_CLIPBOARD_SELECTOR = [
  ".ProseMirror-separator",
  ".collaboration-carets__caret",
  ".collaboration-carets__label",
  ".collaboration-cursor__caret",
  ".collaboration-cursor__label",
  ".tiptap-table-context-controls",
  ".table-controls",
  ".table-selection-overlay-container",
  ".tiptap-table-extend-row-column-button",
  ".tiptap-table-selection-overlay",
  "[data-yjs-cursor]",
].join(",");

const CSS_ONLY_LINE_THROUGH_SELECTOR = [
  '[data-suggestion-type="delete"]',
  '[data-suggestion-type="replaceDeletion"]',
  '[data-diff-type="inline-delete"]',
  '[data-diff-type="block-delete"]',
].join(",");

const shouldKeepProseMirrorWidgetForClipboard = (element: Element) =>
  element.matches(CSS_ONLY_LINE_THROUGH_SELECTOR) ||
  Boolean(element.querySelector(CSS_ONLY_LINE_THROUGH_SELECTOR));

const removeEditorOnlyWidgets = (root: HTMLElement) => {
  root.querySelectorAll(".ProseMirror-widget").forEach((element) => {
    if (!shouldKeepProseMirrorWidgetForClipboard(element)) {
      element.remove();
    }
  });
};

const inlineClipboardLineThroughStyles = (root: HTMLElement) => {
  root
    .querySelectorAll<HTMLElement>(CSS_ONLY_LINE_THROUGH_SELECTOR)
    .forEach((element) => {
      element.style.textDecoration = "line-through";
    });
};

const TABLE_BORDER_STYLE = "1px solid #d0d7de";

const inlineClipboardTableStyles = (root: HTMLElement) => {
  root.querySelectorAll<HTMLTableElement>("table").forEach((table) => {
    table.setAttribute("border", "1");
    table.setAttribute("cellspacing", "0");
    table.style.border = TABLE_BORDER_STYLE;
    table.style.borderCollapse = "collapse";
    table.style.borderSpacing = "0";
  });

  root.querySelectorAll<HTMLElement>("td, th").forEach((cell) => {
    cell.style.border = TABLE_BORDER_STYLE;
    cell.style.padding = "12px 16px";
    cell.style.verticalAlign = "top";
  });

  root.querySelectorAll<HTMLElement>("th").forEach((headerCell) => {
    headerCell.style.fontWeight = "700";
  });
};

export type TiptapClipboardOptions = {
  /**
   * Serialize `text/html` from the document schema (`editor.getHTML()`)
   * instead of the live editor DOM, so dynamic nodes (data keys,
   * conditionals) are copied as re-parseable nodes rather than their
   * resolved NodeView markup. `text/plain` keeps the resolved values.
   */
  copySource?: boolean;
};

const createSourceHtmlContainer = (editor: Editor) => {
  const container = document.createElement("div");
  container.innerHTML = editor.getHTML();
  inlineClipboardTableStyles(container);

  return container;
};

const createClipboardDomClone = (editor: Editor) => {
  const clone = editor.view.dom.cloneNode(true) as HTMLElement;

  clone
    .querySelectorAll(EDITOR_ONLY_CLIPBOARD_SELECTOR)
    .forEach((element) => element.remove());
  removeEditorOnlyWidgets(clone);
  clone
    .querySelectorAll("[contenteditable]")
    .forEach((element) => element.removeAttribute("contenteditable"));
  inlineClipboardLineThroughStyles(clone);
  inlineClipboardTableStyles(clone);

  return clone;
};

export const getTiptapEditorClipboardContent = (
  editor: Editor,
  { copySource = false }: TiptapClipboardOptions = {}
) => {
  const clone = createClipboardDomClone(editor);
  const htmlSource = copySource ? createSourceHtmlContainer(editor) : clone;
  const html = htmlSource.innerHTML.trim() || editor.getHTML();
  const domText =
    typeof clone.innerText === "string" ? clone.innerText : clone.textContent;
  const text = domText?.trim() || editor.getText({ blockSeparator: "\n" });

  return { html, text };
};

export const copyTiptapEditorContent = async (
  editor: Editor,
  options: TiptapClipboardOptions = {}
) => {
  const clipboard = navigator.clipboard;

  if (!clipboard) {
    throw new Error("Clipboard API is not available");
  }

  const { html, text } = getTiptapEditorClipboardContent(editor, options);
  const ClipboardItemConstructor = globalThis.ClipboardItem;
  const canWriteRichClipboard =
    typeof clipboard.write === "function" &&
    typeof ClipboardItemConstructor !== "undefined";

  if (canWriteRichClipboard) {
    try {
      await clipboard.write([
        new ClipboardItemConstructor({
          "text/html": new Blob([html], { type: "text/html" }),
          "text/plain": new Blob([text], { type: "text/plain" }),
        }),
      ]);
      return;
    } catch (error) {
      if (typeof clipboard.writeText !== "function") {
        throw error;
      }
    }
  }

  if (typeof clipboard.writeText !== "function") {
    throw new Error("Clipboard text API is not available");
  }

  await clipboard.writeText(text);
};
